import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle, Play, FlagCheckered, Clock, Package, Lightning, ChartBar, CalendarCheck, Warning, Pause, ArrowClockwise } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatDate } from '../utils/dateFormat';
import { SearchFilterBar, EmptySearchResult, LoadErrorState } from '../components/SearchFilterBar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const PRODUCTION_STAGES = [
  'Profile Cutting', 'Welding', 'Assembling', 'Glass Cutting', 'Glazing', 'Final Output', 'Final Ready to Dispatch'
];

const STATUS_CONFIG = {
  assigned_to_production: { label: 'Assigned', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  under_production: { label: 'In Progress', className: 'bg-purple-50 text-purple-700 border-purple-200' },
  production_completed: { label: 'Completed', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  production_on_hold: { label: 'On Hold', className: 'bg-amber-50 text-amber-700 border-amber-200' }
};

const HOLD_REASONS = [
  'Material Pending',
  'Machine Issue',
  'Manpower Issue',
  'Design Clarification',
  'Client Request',
  'Other'
];

function formatDuration(minutes) {
  if (!minutes) return '--';
  const hrs = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hrs === 0) return `${mins}m`;
  return `${hrs}h ${mins}m`;
}

function formatDateTime(isoString) {
  if (!isoString) return '--';
  const d = new Date(isoString);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || { label: status, className: 'bg-zinc-50 text-zinc-700' };
  return <Badge className={`${config.className} border`}>{config.label}</Badge>;
}

const CHART_TICK_SM = { fontSize: 11 };
const CHART_TICK = { fontSize: 12 };
const DELIVERY_ALERT_DAYS = 2;

function getDaysUntilDelivery(deliveryDate) {
  if (!deliveryDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const delivery = new Date(deliveryDate);
  delivery.setHours(0, 0, 0, 0);
  return Math.ceil((delivery - today) / (1000 * 60 * 60 * 24));
}

function isDeliveryUrgent(deliveryDate) {
  const days = getDaysUntilDelivery(deliveryDate);
  return days !== null && days <= DELIVERY_ALERT_DAYS;
}

function getDeliveryLabel(days) {
  if (days < 0) return `Overdue by ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''}`;
  if (days === 0) return 'Due Today';
  if (days === 1) return 'Due Tomorrow';
  return `Due in ${days} days`;
}

function getUrgencyColor(days) {
  if (days < 0) return 'bg-red-100 border-red-300';
  if (days === 0) return 'bg-orange-100 border-orange-300';
  return 'bg-amber-100 border-amber-300';
}

function getUrgentBorderClass(days) {
  if (days < 0) return 'border-2 border-red-500 ring-2 ring-red-200';
  if (days === 0) return 'border-2 border-orange-500 ring-2 ring-orange-200';
  return 'border-2 border-amber-400 ring-2 ring-amber-200';
}

function getUrgencyMessage(daysLeft) {
  if (daysLeft < 0) return 'OVERDUE';
  if (daysLeft === 0) return 'DUE TODAY';
  return `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`;
}

function DeliveryAlertBadge({ deliveryDate }) {
  const days = getDaysUntilDelivery(deliveryDate);
  if (days === null || days > DELIVERY_ALERT_DAYS) return null;
  return (
    <Badge data-testid="delivery-alert-badge" className="bg-red-100 text-red-800 border border-red-300 animate-pulse">
      <Warning size={14} weight="bold" className="mr-1" />
      {getDeliveryLabel(days)}
    </Badge>
  );
}

function DeliveryAlertBanner({ urgentJobs }) {
  if (urgentJobs.length === 0) return null;
  const overdueCount = urgentJobs.filter(jo => getDaysUntilDelivery(jo.delivery_date) < 0).length;
  const dueTodayCount = urgentJobs.filter(jo => getDaysUntilDelivery(jo.delivery_date) === 0).length;
  const dueSoonCount = urgentJobs.filter(jo => {
    const d = getDaysUntilDelivery(jo.delivery_date);
    return d > 0 && d <= DELIVERY_ALERT_DAYS;
  }).length;

  return (
    <Card data-testid="delivery-alert-banner" className="border-2 border-red-400 rounded-sm shadow-md bg-red-50">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <Warning size={24} weight="bold" className="text-red-600 animate-pulse" />
            </div>
            <div>
              <p className="text-base font-bold text-red-800">Urgent Production Alerts</p>
              <p className="text-sm text-red-600 font-bold">{urgentJobs.length} Job Order{urgentJobs.length !== 1 ? 's' : ''} due within {DELIVERY_ALERT_DAYS} days</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            {overdueCount > 0 && (
              <span className="bg-red-200 text-red-900 text-xs font-bold px-3 py-1.5 rounded-full">
                {overdueCount} Overdue
              </span>
            )}
            {dueTodayCount > 0 && (
              <span className="bg-orange-200 text-orange-900 text-xs font-bold px-3 py-1.5 rounded-full">
                {dueTodayCount} Due Today
              </span>
            )}
            {dueSoonCount > 0 && (
              <span className="bg-amber-200 text-amber-900 text-xs font-bold px-3 py-1.5 rounded-full">
                {dueSoonCount} Due Soon
              </span>
            )}
          </div>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {urgentJobs.map(jo => {
            const days = getDaysUntilDelivery(jo.delivery_date);
            const urgencyColor = getUrgencyColor(days);
            return (
              <div key={jo.jo_number} className={`${urgencyColor} border rounded-sm p-2 text-xs`}>
                <span className="font-bold text-zinc-900">JO #{jo.jo_number}</span>
                <span className="text-zinc-600 mx-1">|</span>
                <span className="text-zinc-700">{jo.customer_name}</span>
                <span className="text-zinc-600 mx-1">|</span>
                <span className="font-bold text-red-700">{formatDate(jo.delivery_date)}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function DashboardStatCard({ label, value, icon: Icon, color, onClick, active }) {
  return (
    <Card
      data-testid={`prod-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`border-zinc-300 rounded-sm shadow-sm transition-all ${
        onClick ? 'cursor-pointer hover:border-orange-400 hover:shadow-md' : ''
      } ${active ? 'border-orange-500 ring-2 ring-orange-200 bg-orange-50/30' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <Icon size={28} weight="bold" className={color} />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
            <p className="text-2xl font-light text-zinc-950">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function getBorderClass(isOnHold, isCompleted, isUrgent, daysLeft) {
  if (isOnHold) return 'border-2 border-amber-400 ring-2 ring-amber-200';
  if (isCompleted) return 'border-2 border-emerald-400';
  if (isUrgent) return getUrgentBorderClass(daysLeft);
  return 'border-zinc-300';
}

function ProductionStageRow({ joNumber, stage, isCompleted, onToggle, disabled }) {
  return (
    <div
      data-testid={`stage-${joNumber}-${stage.toLowerCase().replace(/\s+/g, '-')}`}
      className={`flex items-center space-x-3 p-3 border rounded-sm ${
        isCompleted ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-zinc-300'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <Checkbox
        checked={isCompleted}
        onCheckedChange={() => !disabled && onToggle(joNumber, stage, isCompleted)}
        className="h-5 w-5"
        disabled={disabled}
      />
      <label className={`text-sm font-bold text-zinc-950 flex-1 ${disabled ? '' : 'cursor-pointer'}`}>{stage}</label>
      {isCompleted && <CheckCircle size={20} weight="bold" className="text-emerald-600" />}
    </div>
  );
}

function ProductionJobCard({ jo, stagesData, onStageToggle, onStartProduction, onCompleteProduction, onMarkOnHold, onResumeProduction, actionLoading }) {
  const hasStarted = !!jo.production_start_at;
  const hasCompleted = !!jo.production_end_at;
  const isOnHold = jo.status === 'production_on_hold';
  const isCompleted = jo.status === 'production_completed';
  const canStart = jo.status === 'assigned_to_production' && !hasStarted;
  const allStagesDone = PRODUCTION_STAGES.every(stage => {
    const stages = stagesData[jo.jo_number] || [];
    return stages.find(s => s.stage_name === stage)?.completed;
  });
  const canComplete = hasStarted && !hasCompleted && !isOnHold && allStagesDone;
  const canHold = (jo.status === 'assigned_to_production' || jo.status === 'under_production') && !isCompleted;
  const canResume = isOnHold;
  const urgent = !isCompleted && isDeliveryUrgent(jo.delivery_date);
  const daysLeft = getDaysUntilDelivery(jo.delivery_date);
  const borderClass = getBorderClass(isOnHold, isCompleted, urgent, daysLeft);

  return (
    <Card data-testid={`production-job-${jo.jo_number}`} className={`${borderClass} rounded-sm shadow-sm ${jo.is_urgent ? 'urgent-card-pulse' : ''}`}>
      <CardHeader className="border-b border-zinc-300">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <CardTitle className="text-xl font-bold text-zinc-950">JO #{jo.jo_number}</CardTitle>
            <StatusBadge status={jo.status} />
            {jo.is_urgent && (
              <Badge
                data-testid={`production-urgent-${jo.jo_number}`}
                className="bg-red-600 text-white border border-red-700 urgent-blink"
              >
                <Lightning size={14} weight="bold" className="mr-1" />
                URGENT
              </Badge>
            )}
            {isCompleted && (
              <Badge data-testid={`completed-badge-${jo.jo_number}`} className="bg-emerald-100 text-emerald-800 border border-emerald-300">
                <CheckCircle size={14} weight="bold" className="mr-1" />
                Production Done
              </Badge>
            )}
            {isOnHold && (
              <Badge data-testid={`on-hold-badge-${jo.jo_number}`} className="bg-amber-100 text-amber-800 border border-amber-300 animate-pulse">
                <Pause size={14} weight="bold" className="mr-1" />
                ON HOLD
              </Badge>
            )}
            {jo.priority > 0 && (
              <Badge className="bg-red-50 text-red-700 border border-red-200">Priority {jo.priority}</Badge>
            )}
            {!isCompleted && <DeliveryAlertBadge deliveryDate={jo.delivery_date} />}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {canStart && (
              <Button
                data-testid={`start-production-${jo.jo_number}`}
                onClick={() => onStartProduction(jo.jo_number)}
                disabled={actionLoading}
                className="bg-purple-600 hover:bg-purple-700 text-white h-10"
              >
                <Play size={18} weight="bold" className="mr-2" />
                Start Production
              </Button>
            )}
            {canComplete && (
              <Button
                data-testid={`complete-production-${jo.jo_number}`}
                onClick={() => onCompleteProduction(jo.jo_number)}
                disabled={actionLoading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-10"
              >
                <FlagCheckered size={18} weight="bold" className="mr-2" />
                End Production
              </Button>
            )}
            {canHold && (
              <Button
                data-testid={`hold-production-${jo.jo_number}`}
                onClick={() => onMarkOnHold(jo.jo_number)}
                disabled={actionLoading}
                variant="outline"
                className="border-amber-400 text-amber-700 hover:bg-amber-50 h-10"
              >
                <Pause size={18} weight="bold" className="mr-2" />
                Mark On Hold
              </Button>
            )}
            {canResume && (
              <Button
                data-testid={`resume-production-${jo.jo_number}`}
                onClick={() => onResumeProduction(jo.jo_number)}
                disabled={actionLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white h-10"
              >
                <ArrowClockwise size={18} weight="bold" className="mr-2" />
                Resume Production
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {/* On Hold Banner */}
        {isOnHold && jo.hold_reason && (
          <div data-testid={`hold-banner-${jo.jo_number}`} className="bg-amber-50 border-2 border-amber-300 rounded-sm p-4 mb-4">
            <div className="flex items-start gap-3">
              <Pause size={24} weight="bold" className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-amber-800 text-sm">Production On Hold</p>
                <p className="text-amber-700 mt-1"><span className="font-bold">Reason:</span> {jo.hold_reason}</p>
                {jo.hold_remarks && <p className="text-amber-600 text-sm mt-1"><span className="font-bold">Remarks:</span> {jo.hold_remarks}</p>}
                {jo.hold_by && <p className="text-xs text-amber-500 mt-1">By: {jo.hold_by} at {formatDateTime(jo.hold_at)}</p>}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Customer</p>
            <p className="text-zinc-950 mt-1">{jo.customer_name}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Windows / Sqft</p>
            <p className="text-zinc-950 mt-1">{jo.no_of_windows} units / {jo.total_sqft} sqft</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Delivery Date</p>
            <p className={`mt-1 font-bold ${urgent ? 'text-red-700 text-lg' : 'text-zinc-950'}`}>
              {formatDate(jo.delivery_date) || jo.delivery_date}
            </p>
            {urgent && (
              <p className="text-xs font-bold text-red-600 mt-0.5">
                {getUrgencyMessage(daysLeft)} - Complete Urgently
              </p>
            )}
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Profile / Glass</p>
            <p className="text-zinc-950 mt-1">{jo.profile_type} / {jo.glass_type}</p>
          </div>
        </div>

        {/* Time Tracking Section */}
        {(hasStarted || hasCompleted) && (
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">In Time (Start)</p>
                <p data-testid={`in-time-${jo.jo_number}`} className="text-zinc-950 mt-1 font-bold">{formatDateTime(jo.production_start_at)}</p>
                {jo.production_started_by && <p className="text-xs text-zinc-500">By: {jo.production_started_by}</p>}
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Out Time (End)</p>
                <p data-testid={`out-time-${jo.jo_number}`} className="text-zinc-950 mt-1 font-bold">{formatDateTime(jo.production_end_at)}</p>
                {jo.production_completed_by && <p className="text-xs text-zinc-500">By: {jo.production_completed_by}</p>}
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Time</p>
                <p data-testid={`total-time-${jo.jo_number}`} className="text-zinc-950 mt-1 text-lg font-bold text-purple-700">{formatDuration(jo.production_duration_minutes)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Production Stages - disabled for completed/on-hold */}
        <div className="space-y-3">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Production Stages</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {PRODUCTION_STAGES.map((stage) => {
              const stages = stagesData[jo.jo_number] || [];
              const stageObj = stages.find(s => s.stage_name === stage);
              const stageCompleted = stageObj?.completed || false;
              return (
                <ProductionStageRow
                  key={stage}
                  joNumber={jo.jo_number}
                  stage={stage}
                  isCompleted={stageCompleted}
                  onToggle={onStageToggle}
                  disabled={isCompleted || isOnHold || !hasStarted}
                />
              );
            })}
          </div>
          {!hasStarted && !isCompleted && !isOnHold && (
            <p data-testid={`stages-locked-msg-${jo.jo_number}`} className="text-xs font-bold text-purple-700 mt-1 flex items-center gap-1">
              <Play size={14} weight="bold" /> Click "Start Production" to unlock production stages
            </p>
          )}
          {!allStagesDone && hasStarted && !isCompleted && !isOnHold && (
            <p data-testid={`stages-incomplete-msg-${jo.jo_number}`} className="text-xs text-zinc-500 italic mt-1">
              All 5 stages must be completed before ending production
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const PRODUCTION_SEARCH_FIELDS = [
  { key: 'jo_number', label: 'JO Number', placeholder: 'Enter JO number' },
  { key: 'customer_name', label: 'Customer Name', placeholder: 'Enter customer name' },
  {
    key: 'status', label: 'JO Status', type: 'select',
    options: [
      { value: 'all', label: 'All Statuses' },
      { value: 'assigned_to_production', label: 'Assigned (Pending)' },
      { value: 'under_production', label: 'Under Production' },
      { value: 'production_on_hold', label: 'On Hold' },
      { value: 'production_completed', label: 'Production Completed' },
    ]
  }
];

const PRODUCTION_DEFAULT_FILTERS = { jo_number: '', customer_name: '', status: 'all' };

export function Production() {
  const [jobOrders, setJobOrders] = useState([]);
  const [stagesData, setStagesData] = useState({});
  const [loading, setLoading] = useState(true);
  const [dashStats, setDashStats] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [daySummary, setDaySummary] = useState([]);
  const [showDaySummary, setShowDaySummary] = useState(false);
  const [filters, setFilters] = useState(PRODUCTION_DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(PRODUCTION_DEFAULT_FILTERS);
  const [loadError, setLoadError] = useState(false);
  const [holdDialog, setHoldDialog] = useState({ open: false, joNumber: null });
  const [holdForm, setHoldForm] = useState({ hold_reason: '', hold_remarks: '' });
  const jobListRef = useRef(null);

  const fetchProductionJobs = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [jobsRes, statsRes, dayRes] = await Promise.all([
        api.get(`/api/job-orders?list_type=production_all`),
        api.get(`/api/production/dashboard-stats`),
        api.get(`/api/production/day-summary?days=30`)
      ]);
      const productionJobs = jobsRes.data;
      setJobOrders(productionJobs);
      setDashStats(statsRes.data);
      setDaySummary(dayRes.data);

      // Batch fetch stages for active (non-completed) jobs
      const activeJobs = productionJobs.filter(jo => jo.status !== 'production_completed');
      if (activeJobs.length > 0) {
        const joNumbers = activeJobs.map(jo => jo.jo_number);
        const { data: stagesMap } = await api.post(`/api/production/stages-batch`, { jo_numbers: joNumbers });
        setStagesData(stagesMap);
      } else {
        setStagesData({});
      }
    } catch (err) {
      if (err?.isAuthError) return;
      setLoadError(true);
      toast.error('Failed to load production data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProductionJobs();
  }, [fetchProductionJobs]);

  const handleSearch = () => { setAppliedFilters({...filters}); };
  const handleReset = (cleared) => { setFilters(cleared); setAppliedFilters(cleared); };
  const handleRefresh = () => { setLoadError(false); fetchProductionJobs(); };

  const handleStageToggle = useCallback(async (jo_number, stageName, currentStatus) => {
    try {
      await api.post(
        `/api/job-orders/${jo_number}/production-stage`,
        { stage_name: stageName, completed: !currentStatus, remarks: '' },
        { withCredentials: true }
      );
      toast.success(`Stage ${!currentStatus ? 'completed' : 'uncompleted'}`);
      fetchProductionJobs();
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err.response?.data?.detail || 'Failed to update stage');
    }
  }, [fetchProductionJobs]);

  const handleStartProduction = useCallback(async (jo_number) => {
    setActionLoading(true);
    try {
      await api.post(`/api/job-orders/${jo_number}/start-production`, {});
      toast.success('Production started');
      fetchProductionJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start production');
    } finally {
      setActionLoading(false);
    }
  }, [fetchProductionJobs]);

  const handleCompleteProduction = useCallback(async (jo_number) => {
    setActionLoading(true);
    try {
      await api.post(`/api/job-orders/${jo_number}/complete-production`, {});
      toast.success('Production completed successfully');
      fetchProductionJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete production');
    } finally {
      setActionLoading(false);
    }
  }, [fetchProductionJobs]);

  const handleMarkOnHold = useCallback((jo_number) => {
    setHoldForm({ hold_reason: '', hold_remarks: '' });
    setHoldDialog({ open: true, joNumber: jo_number });
  }, []);

  const handleConfirmHold = useCallback(async () => {
    if (!holdForm.hold_reason) {
      toast.error('Hold reason is mandatory');
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/api/job-orders/${holdDialog.joNumber}/production-on-hold`, holdForm);
      toast.success('Production marked on hold');
      setHoldDialog({ open: false, joNumber: null });
      fetchProductionJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark on hold');
    } finally {
      setActionLoading(false);
    }
  }, [holdForm, holdDialog.joNumber, fetchProductionJobs]);

  const handleResumeProduction = useCallback(async (jo_number) => {
    setActionLoading(true);
    try {
      await api.post(`/api/job-orders/${jo_number}/resume-production`, {});
      toast.success('Production resumed');
      fetchProductionJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume production');
    } finally {
      setActionLoading(false);
    }
  }, [fetchProductionJobs]);

  const handleStatCardClick = useCallback((statusValue) => {
    const newStatus = appliedFilters.status === statusValue ? 'all' : statusValue;
    setFilters(f => ({...f, status: newStatus}));
    setAppliedFilters(f => ({...f, status: newStatus}));
    if (newStatus !== 'all') {
      setTimeout(() => jobListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [appliedFilters.status]);

  const sortedJobOrders = useMemo(
    () => [...jobOrders].sort((a, b) => {
      // On Hold jobs at top after urgent
      const statusOrder = { production_on_hold: 0, assigned_to_production: 1, under_production: 1, production_completed: 2 };
      const aOrder = statusOrder[a.status] ?? 1;
      const bOrder = statusOrder[b.status] ?? 1;
      if (aOrder !== bOrder) return aOrder - bOrder;
      const aUrgent = isDeliveryUrgent(a.delivery_date) ? 0 : 1;
      const bUrgent = isDeliveryUrgent(b.delivery_date) ? 0 : 1;
      if (aUrgent !== bUrgent) return aUrgent - bUrgent;
      return new Date(a.delivery_date) - new Date(b.delivery_date);
    }),
    [jobOrders]
  );

  // By default show only active (non-completed) jobs; show completed only when explicitly filtered
  const filteredJobOrders = useMemo(() => {
    return sortedJobOrders.filter(jo => {
      if (appliedFilters.jo_number && !jo.jo_number.toLowerCase().includes(appliedFilters.jo_number.toLowerCase())) return false;
      if (appliedFilters.customer_name && !jo.customer_name.toLowerCase().includes(appliedFilters.customer_name.toLowerCase())) return false;
      if (appliedFilters.status && appliedFilters.status !== 'all') {
        return jo.status === appliedFilters.status;
      }
      // Default: hide completed from active list
      return jo.status !== 'production_completed';
    });
  }, [sortedJobOrders, appliedFilters]);

  const urgentJobs = useMemo(
    () => jobOrders.filter(jo => jo.status !== 'production_completed' && isDeliveryUrgent(jo.delivery_date)),
    [jobOrders]
  );

  function renderJobList() {
    if (loading) {
      return <div className="text-center py-12">Loading production jobs...</div>;
    }
    if (loadError) {
      return <LoadErrorState onRetry={handleRefresh} />;
    }
    if (filteredJobOrders.length === 0) {
      return <EmptySearchResult onReset={() => handleReset(PRODUCTION_DEFAULT_FILTERS)} />;
    }
    return (
      <div className="space-y-4">
        {filteredJobOrders.map((jo) => (
          <ProductionJobCard
            key={jo.jo_number}
            jo={jo}
            stagesData={stagesData}
            onStageToggle={handleStageToggle}
            onStartProduction={handleStartProduction}
            onCompleteProduction={handleCompleteProduction}
            onMarkOnHold={handleMarkOnHold}
            onResumeProduction={handleResumeProduction}
            actionLoading={actionLoading}
          />
        ))}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div>
          <h1 className="text-4xl font-light tracking-tight text-zinc-950">Production Tracking</h1>
          <p className="text-sm text-zinc-500 mt-2">Track time and update production stages</p>
        </div>

        {/* Admin-flagged urgent JOs banner */}
        {(() => {
          const urgentJOs = jobOrders.filter(j => j.is_urgent && j.status !== 'production_completed');
          if (urgentJOs.length === 0) return null;
          return (
            <div
              data-testid="production-urgent-banner"
              className="bg-red-600 text-white border-2 border-red-700 rounded-sm px-4 py-3 urgent-card-pulse flex items-center gap-3 flex-wrap"
            >
              <Lightning size={24} weight="fill" className="urgent-blink" />
              <span className="font-bold uppercase tracking-wide urgent-blink">
                {urgentJOs.length} URGENT Job Order{urgentJOs.length === 1 ? '' : 's'} from Admin
              </span>
              <span className="text-sm opacity-90">
                {urgentJOs.slice(0, 5).map(j => `#${j.jo_number}`).join(', ')}{urgentJOs.length > 5 ? `, +${urgentJOs.length - 5} more` : ''}
              </span>
            </div>
          );
        })()}

        {/* Production Dashboard Stats */}
        {dashStats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <DashboardStatCard label="Assigned (Pending)" value={dashStats.assigned_pending} icon={Package} color="text-blue-600"
              onClick={() => handleStatCardClick('assigned_to_production')}
              active={appliedFilters.status === 'assigned_to_production'}
            />
            <DashboardStatCard label="Under Production" value={dashStats.under_production} icon={Lightning} color="text-purple-600"
              onClick={() => handleStatCardClick('under_production')}
              active={appliedFilters.status === 'under_production'}
            />
            <DashboardStatCard label="On Hold" value={dashStats.on_hold} icon={Pause} color="text-amber-600"
              onClick={() => handleStatCardClick('production_on_hold')}
              active={appliedFilters.status === 'production_on_hold'}
            />
            <DashboardStatCard label="Completed Today" value={dashStats.completed_today} icon={CheckCircle} color="text-emerald-600"
              onClick={() => handleStatCardClick('production_completed')}
              active={appliedFilters.status === 'production_completed'}
            />
            <DashboardStatCard label="Windows Today" value={dashStats.windows_today} icon={ChartBar} color="text-orange-600" />
            <DashboardStatCard label="Sqft Today" value={dashStats.sqft_today} icon={ChartBar} color="text-cyan-600" />
            <DashboardStatCard label="Avg Time Today" value={formatDuration(dashStats.avg_time_minutes_today)} icon={Clock} color="text-zinc-600" />
          </div>
        )}

        {/* Delivery Alert Banner */}
        <DeliveryAlertBanner urgentJobs={urgentJobs} />

        {/* Search & Filters */}
        <SearchFilterBar
          fields={PRODUCTION_SEARCH_FIELDS}
          filters={filters}
          setFilters={setFilters}
          onSearch={handleSearch}
          onReset={handleReset}
          onRefresh={handleRefresh}
          loading={loading}
          resultCount={filteredJobOrders.length}
        />

        {/* Day-wise Production Summary Toggle */}
        <div className="flex items-center justify-between">
          <Button
            data-testid="toggle-day-summary"
            onClick={() => setShowDaySummary(!showDaySummary)}
            variant={showDaySummary ? 'default' : 'outline'}
            className={showDaySummary ? 'bg-zinc-900 hover:bg-zinc-800 text-white h-10' : 'h-10'}
          >
            <CalendarCheck size={18} weight="bold" className="mr-2" />
            {showDaySummary ? 'Hide' : 'Show'} Day-wise Summary
          </Button>
        </div>

        {/* Day-wise Production Summary */}
        {showDaySummary && daySummary.length > 0 && (
          <>
            <Card data-testid="day-summary-chart" className="border-zinc-300 rounded-sm shadow-sm">
              <CardHeader className="border-b border-zinc-300">
                <CardTitle className="text-xl font-bold text-zinc-950">Daily Production (Last 30 Days)</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div style={{ height: 300, width: '100%' }}>
                  <ResponsiveContainer>
                    <BarChart data={[...daySummary].reverse()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="_id" tick={CHART_TICK_SM} angle={-45} textAnchor="end" height={60} />
                      <YAxis tick={CHART_TICK} />
                      <Tooltip />
                      <Bar dataKey="jobs_completed" fill="#FF6B35" name="Jobs Completed" />
                      <Bar dataKey="total_windows" fill="#7c3aed" name="Windows" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="day-summary-table" className="border-zinc-300 rounded-sm shadow-sm">
              <CardHeader className="border-b border-zinc-300">
                <CardTitle className="text-xl font-bold text-zinc-950">Day-wise Production Details</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50">
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Date</th>
                        <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Jobs Completed</th>
                        <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Windows</th>
                        <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Sqft</th>
                        <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Avg Time</th>
                        <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Total Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {daySummary.map((day) => (
                        <tr key={day._id} className="border-b border-zinc-100 hover:bg-zinc-50">
                          <td className="p-3 font-bold text-zinc-950">{day._id}</td>
                          <td className="p-3 text-center text-zinc-700">{day.jobs_completed}</td>
                          <td className="p-3 text-center text-zinc-700">{day.total_windows}</td>
                          <td className="p-3 text-center text-zinc-700">{day.total_sqft}</td>
                          <td className="p-3 text-center text-purple-700 font-bold">{formatDuration(day.avg_duration)}</td>
                          <td className="p-3 text-center text-zinc-700">{formatDuration(day.total_duration)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {showDaySummary && daySummary.length === 0 && (
          <Card className="border-zinc-300 rounded-sm shadow-sm">
            <CardContent className="p-8 text-center text-zinc-500">No production completions recorded yet</CardContent>
          </Card>
        )}

        <div ref={jobListRef}>
          {renderJobList()}
        </div>

        {/* On Hold Dialog */}
        <Dialog open={holdDialog.open} onOpenChange={(open) => !open && setHoldDialog({ open: false, joNumber: null })}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-zinc-950">Mark Production On Hold</DialogTitle>
              <DialogDescription className="text-sm text-zinc-500">
                JO #{holdDialog.joNumber} — Select a reason and optionally add remarks
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <label className="text-sm font-bold text-zinc-700 mb-1.5 block">Hold Reason <span className="text-red-500">*</span></label>
                <Select
                  value={holdForm.hold_reason}
                  onValueChange={(val) => setHoldForm(prev => ({ ...prev, hold_reason: val }))}
                >
                  <SelectTrigger data-testid="hold-reason-select" className="h-11">
                    <SelectValue placeholder="Select reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {HOLD_REASONS.map(reason => (
                      <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-bold text-zinc-700 mb-1.5 block">Remarks (Optional)</label>
                <Textarea
                  data-testid="hold-remarks-input"
                  value={holdForm.hold_remarks}
                  onChange={(e) => setHoldForm(prev => ({ ...prev, hold_remarks: e.target.value }))}
                  placeholder="Add additional details..."
                  className="min-h-20"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setHoldDialog({ open: false, joNumber: null })}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="confirm-hold-button"
                  onClick={handleConfirmHold}
                  disabled={!holdForm.hold_reason || actionLoading}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Pause size={18} weight="bold" className="mr-2" />
                  Confirm Hold
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
